import React, { useMemo, useRef, useState } from 'react';
import { api } from '../../services/api';

function unwrapList(resp) {
  if (Array.isArray(resp?.data)) return resp.data;
  if (resp?.data) return [resp.data];
  return [];
}

export function SerialQueryModule({ assignments = [], materials = [], movements = [], warehouses = [], people = [] }) {
  const [serialInput, setSerialInput] = useState('');
  const [statusMsg, setStatusMsg] = useState(null);
  const [result, setResult] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  // Una sola bandera: no pintar resumen ni histórico hasta que ambos estén listos.
  const [consultLoading, setConsultLoading] = useState(false);
  const requestSeqRef = useRef(0);

  const materialById = useMemo(() => {
    const map = new Map();
    (materials || []).forEach((m) => map.set(m.id, m));
    return map;
  }, [materials]);

  const warehouseById = useMemo(() => {
    const map = new Map();
    (warehouses || []).forEach((w) => map.set(w.id, w));
    return map;
  }, [warehouses]);

  const normalizeSerial = (s) => String(s || '').trim();

  const normMovType = (raw) => {
    let t = String(raw || '').toLowerCase().trim();
    if (t === 'asignación') t = 'asignacion';
    if (t === 'devolución') t = 'devolucion';
    if (t === 'préstamo') t = 'prestamo';
    return t;
  };

  const fetchMovementsBySerial = async (serial) => {
    const page = 1000;
    const maxPages = 30;
    let acc = [];
    for (let p = 0; p < maxPages; p += 1) {
      const off = p * page;
      const resp = await api.movements.get({ serial, order: 'asc', limit: page, offset: off });
      const rows = unwrapList(resp);
      if (rows.length === 0) break;
      acc = acc.concat(rows);
      if (rows.length < page) break;
    }
    return acc;
  };

  const fmtDateTime = (raw) => {
    const str = String(raw || '');
    if (!str) return '';
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(str)) return str.slice(0, 19);
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(str)) {
      try {
        return new Date(str).toLocaleString('es-CO', { timeZone: 'America/Bogota' });
      } catch (_) {
        return str.replace('T', ' ').slice(0, 19);
      }
    }
    try {
      return new Date(str).toLocaleString('es-CO', { timeZone: 'America/Bogota' });
    } catch (_) {
      return str;
    }
  };

  const fmtHistoryRow = (mv) => {
    const t = String(mv.type || '').toLowerCase();
    const typeUi = t === 'entrada' ? 'Entrada'
      : t === 'asignacion' ? 'Asignación'
      : t === 'devolucion' ? 'Devolución'
      : t === 'prestamo' ? 'Préstamo'
      : t === 'consumible' ? 'Consumible'
      : 'Salida';
    const wid = mv.warehouse_id || mv.warehouseId || '';
    const wh = warehouseById.get(wid) || null;
    const d = mv.created_at ? fmtDateTime(mv.created_at) : (mv.date || '');
    return {
      id: mv.id,
      date: d,
      type: typeUi,
      warehouseName: wh?.name || wid || '',
      responsibleName: mv.requester_name || mv.responsibleName || '',
      responsibleDoc: mv.requester_dni || mv.dni || mv.responsibleDoc || '',
      quantity: mv.quantity,
      user: mv.performed_by_name || mv.performed_by_email || '',
      observations: mv.notes || mv.observations || '',
    };
  };

  const buildResultFromApiMovement = (mv, status, serialFallback) => {
    const mid = Number(mv.material_id ?? mv.materialId);
    const mat = materialById.get(mid) || null;
    const wid = mv.warehouse_id || mv.warehouseId || '';
    const wh = warehouseById.get(wid) || null;
    const owner = String(mv.requester_dni || mv.dni || mv.responsibleDoc || '').trim();
    const serialVal = normalizeSerial(mv.serial) || normalizeSerial(serialFallback);
    const resolvedStatus = status || (owner ? 'Asignado' : 'Disponible');
    return {
      status: resolvedStatus,
      serial: serialVal,
      materialCode: mat?.code || mv.material_code || mv.materialCode || '',
      materialName: mat?.name || mv.material_name || mv.materialName || '',
      warehouseId: wid,
      warehouseName: wh?.name || wid || '',
      responsibleName: mv.requester_name || mv.responsibleName || (owner ? (people.find((p) => String(p.dni || p.id) === owner)?.name || '') : ''),
      responsibleDoc: owner,
      certificateNo: '',
    };
  };

  const resolveStateFromMovementRows = (rows, serialFallback) => {
    if (!rows || rows.length === 0) return null;
    const sorted = [...rows].sort((a, b) => Number(a.id) - Number(b.id));
    let netAssigned = false;
    let lastAssignMv = null;
    for (const mv of sorted) {
      const t = normMovType(mv.type);
      if (t === 'asignacion' || t === 'prestamo') {
        netAssigned = true;
        lastAssignMv = mv;
      } else if (t === 'devolucion' || t === 'salida' || t === 'consumible') {
        netAssigned = false;
        lastAssignMv = null;
      }
    }
    if (netAssigned && lastAssignMv) {
      return buildResultFromApiMovement(lastAssignMv, 'Asignado', serialFallback);
    }
    const last = sorted[sorted.length - 1];
    const lt = normMovType(last.type);
    if (lt === 'entrada' || lt === 'devolucion') {
      return buildResultFromApiMovement(last, 'Disponible', serialFallback);
    }
    if (lt === 'salida' || lt === 'consumible') {
      return buildResultFromApiMovement(last, 'Salida', serialFallback);
    }
    return buildResultFromApiMovement(last, lt === 'asignacion' || lt === 'prestamo' ? 'Asignado' : 'Disponible', serialFallback);
  };

  const mapAssignmentApiToResult = (a, s) => {
    const mid = Number(a.material_id);
    const mat = materialById.get(mid) || null;
    const wid = a.warehouse_id || '';
    const wh = warehouseById.get(wid) || null;
    const doc = a.person_dni || a.person_id || '';
    const qty = Number(a.quantity) || 0;
    return {
      status: qty > 0 ? 'Asignado' : 'Devuelto',
      serial: s,
      materialCode: mat?.code || a.material_code || '',
      materialName: mat?.name || a.material_name || '',
      warehouseId: wid,
      warehouseName: wh?.name || wid || '',
      responsibleName: a.person_name || String(doc || ''),
      responsibleDoc: String(doc || ''),
      certificateNo: '',
    };
  };

  const resultFromLocalAssignment = (s) => {
    const list = (assignments || []).filter((a) => a.status === 'Asignado' && normalizeSerial(a.serial) === s);
    if (list.length === 0) return null;
    const withIdx = list.map((a, idx) => ({ idx, a, ts: Date.parse(a.assignDate || a.date || '') || 0 }));
    withIdx.sort((x, y) => (y.ts - x.ts));
    const assigned = withIdx[0].a;
    const mat = materialById.get(assigned.materialId) || null;
    const wh = warehouseById.get(assigned.warehouseId) || null;
    return {
      status: 'Asignado',
      serial: s,
      materialCode: mat?.code || '',
      materialName: mat?.name || assigned.materialName || '',
      warehouseId: assigned.warehouseId || '',
      warehouseName: wh?.name || assigned.warehouseId || '',
      responsibleName: assigned.responsibleName || '',
      responsibleDoc: assigned.responsibleDoc || '',
      certificateNo: '',
    };
  };

  const resultFromLocalMovements = (s) => {
    const assignedList = (movements || []).filter((mv) => {
      if (normalizeSerial(mv.serial) !== s) return false;
      const t = normMovType(mv.type);
      return t === 'asignacion' || t === 'prestamo';
    });
    if (assignedList.length > 0) {
      const withIdx = assignedList.map((mv, idx) => ({
        idx,
        mv,
        ts: Date.parse(mv.date || mv.created_at || '') || Number(mv.id) || 0,
      }));
      withIdx.sort((x, y) => (y.ts - x.ts));
      return buildResultFromApiMovement(withIdx[0].mv, 'Asignado', s);
    }
    const entryList = (movements || []).filter((mv) => mv.type === 'Entrada' && normalizeSerial(mv.serial) === s);
    if (entryList.length === 0) return null;
    const withIdx = entryList.map((mv, idx) => ({ idx, mv, ts: Date.parse(mv.date || '') || 0 }));
    withIdx.sort((x, y) => (y.ts - x.ts));
    const entry = withIdx[0].mv;
    const mat = materialById.get(entry.materialId) || null;
    const wh = warehouseById.get(entry.warehouseId) || null;
    return {
      status: 'Disponible',
      serial: s,
      materialCode: mat?.code || entry.materialCode || '',
      materialName: mat?.name || entry.materialName || '',
      warehouseId: entry.warehouseId || '',
      warehouseName: wh?.name || entry.warehouseId || '',
      responsibleName: '',
      responsibleDoc: '',
      certificateNo: '',
    };
  };

  const resultFromSerialRow = (row, s) => {
    if (!row) return null;
    const wh2 = warehouseById.get(row.warehouse_id) || null;
    const mat2 = materialById.get(row.material_id) || null;
    const owner = String(row.owner_dni || '').trim();
    const rowStatus = String(row.status || '').trim();
    return {
      status: rowStatus || (owner ? 'Asignado' : 'Disponible'),
      serial: s,
      materialCode: mat2?.code || row.material_code || '',
      materialName: mat2?.name || row.materialName || '',
      warehouseId: row.warehouse_id || '',
      warehouseName: wh2?.name || row.warehouse_name || '',
      responsibleDoc: owner || '',
      responsibleName: owner ? (people.find((p) => String(p.dni || p.id) === owner)?.name || '') : '',
      certificateNo: row.certificate_no || '',
    };
  };

  /** Une certificado / metadatos de serials sin pintar a medias. */
  const enrichWithSerialRow = (base, row) => {
    if (!base || !row) return base;
    const wh2 = warehouseById.get(row.warehouse_id) || null;
    const mat2 = materialById.get(row.material_id) || null;
    const owner = String(row.owner_dni || '').trim();
    const rowStatus = String(row.status || '').trim();
    return {
      ...base,
      status: base.status || rowStatus || (owner ? 'Asignado' : base.status),
      materialCode: base.materialCode || mat2?.code || '',
      materialName: base.materialName || row.materialName || mat2?.name || '',
      warehouseId: base.warehouseId || row.warehouse_id || '',
      warehouseName: base.warehouseName || wh2?.name || row.warehouse_name || '',
      responsibleDoc: base.responsibleDoc || owner || '',
      responsibleName: base.responsibleName
        || (owner ? (people.find((p) => String(p.dni || p.id) === owner)?.name || '') : ''),
      certificateNo: base.certificateNo || row.certificate_no || '',
    };
  };

  const loadHistoryRows = async (s, apiMovsAlready) => {
    const syn = s.match(/^NS-(\d+)-\d+$/);
    if (syn) {
      const targetId = parseInt(syn[1], 10);
      const fromApi = (apiMovsAlready || []).find((m) => Number(m.id) === targetId);
      if (fromApi) return [fmtHistoryRow(fromApi)];
      try {
        const resp = await api.movements.get({ order: 'asc', limit: 1000 });
        const arr = unwrapList(resp);
        const mv = arr.find((m) => Number(m.id) === targetId);
        return mv ? [fmtHistoryRow(mv)] : [];
      } catch (_) {
        return [];
      }
    }

    if (apiMovsAlready && apiMovsAlready.length > 0) {
      return apiMovsAlready.map(fmtHistoryRow);
    }

    // Fallback: brand_item_id cuando el serial no vino en movements?serial=
    try {
      const biResp = await api.brandItems.get({ q: s, order: 'asc', limit: 50 });
      const biArr = unwrapList(biResp);
      const biExact = biArr.find((r) => String(r.serial || '').trim() === s) || null;
      if (biExact) {
        const respAll = await api.movements.get({ order: 'asc', limit: 1000 });
        const arrAll = unwrapList(respAll);
        return arrAll
          .filter((mv) => Number(mv.brand_item_id || 0) === Number(biExact.id))
          .map(fmtHistoryRow);
      }
    } catch (_) { /* fallback local */ }

    return (movements || [])
      .filter((mv) => normalizeSerial(mv.serial) === s)
      .map(fmtHistoryRow);
  };

  const resolveResultSnapshot = (s, asgArr, apiMovs, serialRow) => {
    const activeAsg = (asgArr || []).find((a) => (Number(a.quantity) || 0) > 0) || null;
    if (activeAsg) {
      return enrichWithSerialRow(mapAssignmentApiToResult(activeAsg, s), serialRow);
    }

    const localAsg = resultFromLocalAssignment(s);
    if (localAsg) {
      return enrichWithSerialRow(localAsg, serialRow);
    }

    const fromApiMovs = resolveStateFromMovementRows(apiMovs, s);
    if (fromApiMovs) {
      return enrichWithSerialRow(fromApiMovs, serialRow);
    }

    const fromLocalMovs = resultFromLocalMovements(s);
    if (fromLocalMovs) {
      return enrichWithSerialRow(fromLocalMovs, serialRow);
    }

    if (serialRow) {
      return resultFromSerialRow(serialRow, s);
    }

    return null;
  };

  const onConsult = async () => {
    const s = normalizeSerial(serialInput);
    if (!s) {
      setStatusMsg({ type: 'warn', text: 'Ingrese un serial' });
      return;
    }

    const requestId = ++requestSeqRef.current;
    // Reset inmediato: evita mostrar el serial anterior mientras carga el nuevo.
    setStatusMsg(null);
    setResult(null);
    setHistoryRows([]);
    setConsultLoading(true);

    try {
      const [asgSettled, movSettled, serialSettled] = await Promise.allSettled([
        api.assignments.get({ serial: s, limit: 20, order: 'desc' }),
        fetchMovementsBySerial(s),
        api.serials.list({ serial: s, include_all: 1, include_deleted: 1, limit: 1 }),
      ]);

      if (requestId !== requestSeqRef.current) return;

      const asgArr = asgSettled.status === 'fulfilled' ? unwrapList(asgSettled.value) : [];
      const apiMovs = movSettled.status === 'fulfilled' ? (movSettled.value || []) : [];
      const serialArr = serialSettled.status === 'fulfilled' ? unwrapList(serialSettled.value) : [];
      const serialRow = serialArr[0] || null;

      const history = await loadHistoryRows(s, apiMovs);
      if (requestId !== requestSeqRef.current) return;

      const nextResult = resolveResultSnapshot(s, asgArr, apiMovs, serialRow);

      // Commit atómico: resumen + histórico juntos (o vacío + aviso).
      setResult(nextResult);
      setHistoryRows(history);
      setStatusMsg(
        nextResult
          ? null
          : { type: 'warn', text: 'No se encontró información para el serial ingresado' }
      );
    } catch (_) {
      if (requestId !== requestSeqRef.current) return;
      setResult(null);
      setHistoryRows([]);
      setStatusMsg({ type: 'warn', text: 'No se encontró información para el serial ingresado' });
    } finally {
      if (requestId === requestSeqRef.current) {
        setConsultLoading(false);
      }
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Consulta por Serial</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <div>
          <label className="block text-sm font-medium mb-1">Serial</label>
          <input
            type="text"
            value={serialInput}
            onChange={(e) => setSerialInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onConsult();
              }
            }}
            className="border rounded px-3 py-2 text-sm w-full"
            placeholder="Ingrese serial exacto"
            disabled={consultLoading}
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onConsult}
            disabled={consultLoading}
            className="px-3 py-2 rounded-md text-sm font-medium focus:outline-none border border-gray-300 hover:bg-gray-50 disabled:opacity-60"
            style={{ backgroundColor: '#fff', color: '#333' }}
          >
            {consultLoading ? 'Consultando…' : 'Consultar'}
          </button>
          {!consultLoading && statusMsg && (
            <span className={`ml-3 text-sm ${statusMsg.type === 'warn' ? 'text-yellow-700' : 'text-green-700'}`}>{statusMsg.text}</span>
          )}
        </div>
      </div>

      {consultLoading && (
        <div className="text-sm text-gray-500 border rounded p-4 bg-gray-50">
          Cargando resumen e histórico del serial…
        </div>
      )}

      {!consultLoading && result && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border rounded p-3">
              <div className="text-sm text-gray-500">Estado</div>
              <div className="text-base font-semibold">{result.status}</div>
            </div>
            <div className="border rounded p-3">
              <div className="text-sm text-gray-500">Serial</div>
              <div className="text-base font-semibold">{result.serial}</div>
            </div>
            <div className="border rounded p-3">
              <div className="text-sm text-gray-500">Código de material</div>
              <div className="text-base font-semibold">{result.materialCode || '—'}</div>
            </div>
            <div className="border rounded p-3">
              <div className="text-sm text-gray-500">Nombre de material</div>
              <div className="text-base font-semibold">{result.materialName || '—'}</div>
            </div>
            <div className="border rounded p-3">
              <div className="text-sm text-gray-500">Bodega</div>
              <div className="text-base font-semibold">{result.warehouseName || result.warehouseId || '—'}</div>
            </div>
            <div className="border rounded p-3">
              <div className="text-sm text-gray-500">Certificado</div>
              <div className="text-base font-semibold">{result.certificateNo || '—'}</div>
            </div>
          </div>

          {result.status === 'Asignado' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="border rounded p-3">
                <div className="text-sm text-gray-500">Asignado a</div>
                <div className="text-base font-semibold">{result.responsibleName || '—'}</div>
              </div>
              <div className="border rounded p-3">
                <div className="text-sm text-gray-500">Cédula</div>
                <div className="text-base font-semibold">{result.responsibleDoc || '—'}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {!consultLoading && (
        <div className="space-y-2">
          <div className="text-sm font-semibold">Histórico de Movimientos</div>
          {historyRows.length > 0 && (() => {
            const firstEntry = historyRows.find((r) => r.type === 'Entrada');
            if (!firstEntry) return null;
            return (
              <div className="text-xs text-gray-700">Entrada inicial: {firstEntry.date} · {firstEntry.warehouseName}</div>
            );
          })()}
          <div className="overflow-x-auto border rounded">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-100 text-gray-700">
                  <th className="px-4 py-2 text-left">Fecha</th>
                  <th className="px-4 py-2 text-left">Tipo</th>
                  <th className="px-4 py-2 text-left">Bodega</th>
                  <th className="px-4 py-2 text-left">Responsable</th>
                  <th className="px-4 py-2 text-left">Cantidad</th>
                  <th className="px-4 py-2 text-left">Realizado por</th>
                  <th className="px-4 py-2 text-left">Observaciones</th>
                </tr>
              </thead>
              <tbody>
                {historyRows.length === 0 ? (
                  <tr>
                    <td className="px-4 py-3 text-gray-500" colSpan={7}>No hay movimientos para este serial.</td>
                  </tr>
                ) : (
                  historyRows.map((r) => (
                    <tr key={r.id} className="border-t">
                      <td className="px-4 py-3">{r.date}</td>
                      <td className="px-4 py-3">{r.type}</td>
                      <td className="px-4 py-3">{r.warehouseName}</td>
                      <td className="px-4 py-3">{r.responsibleDoc ? `${r.responsibleName || ''} (${r.responsibleDoc})` : (r.responsibleName || '')}</td>
                      <td className="px-4 py-3">{r.quantity}</td>
                      <td className="px-4 py-3">{r.user || '-'}</td>
                      <td className="px-4 py-3">{r.observations}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
